import { TeamGameAggregate } from '@statsbomb/parachute-types';
import i18next from 'i18next';
import { atom } from 'jotai';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { competitionOptionsAtom } from '@/atoms/filters/highLevel/competitions';
import { unwrappedTeamSelectedGamesForQueryAtom } from '@/atoms/filters/team/teamGames';
import { pageAndOrderParamsAtom } from '@/atoms/general';
import { fetchClientAtom } from '@/atoms/queries/client';
import { teamGamesAtom } from '@/atoms/queries/teams';
import { clientOrderByAtom, clientOrderDirectionAtom } from '@/atoms/table';
import { teamIdAtom } from '@/atoms/team/team';
import { ALL_ITEMS } from '@/consts/interface';
import { teamGamesTotalAggregatesUrl } from '@/query/url';
import { convertOptionsToValues, limitArray, sortObjectArray } from '@/utils/array';
import { flattenAndAddPrefix } from '@/utils/object';
import { objSnakeToCamel } from '@/utils/queries';

export const rawTeamGameAggsDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamGameAggs', get(teamIdAtom)] as const;
  const queryFn = async ({ queryKey: [, teamId] }: { queryKey: typeof queryKey }) => {
    const url = teamGamesTotalAggregatesUrl(teamId, {
      limit: ALL_ITEMS,
    });
    if (!url) return [];

    const data: TeamGameAggregate[] = await fetch(url);

    return data.map(objSnakeToCamel);
  };
  return { queryKey, queryFn };
});

/* need to do joining here between teamGames and rawTeamGameAggsDataAtom as we don't have 
the aggregate data for all our games, so when using the lastXGames filter it didn't work as expected.
This joins them together so the filter works, and we display a message for when the aggregates haven't
been processed yet. */
export const teamGameAggsAtom = atom(async get => {
  const orderBy = get(clientOrderByAtom);
  const orderDirection = get(clientOrderDirectionAtom);
  const pageAndOrderParams = get(pageAndOrderParamsAtom);
  const teamGameAggs = (await get(rawTeamGameAggsDataAtom)).data;
  const competitionOptions = await get(competitionOptionsAtom);
  const unwrappedTeamGamesForQuery = convertOptionsToValues(get(unwrappedTeamSelectedGamesForQueryAtom));
  const teamGames = await get(teamGamesAtom);

  const gamesToDisplayWithAggs = teamGames
    .filter(({ game }) => unwrappedTeamGamesForQuery.includes(game.gameId))
    .map(game => {
      const gameAggs = teamGameAggs.find(gameAgg => gameAgg.game.gameId === game.game.gameId);
      const competition = competitionOptions.find(comp => game.cycle.competitionId === comp.value);
      const gameWithAggs = {
        ...game,
        aggregates: gameAggs?.aggregates,
        hasAggregates: !!gameAggs,
        competition: { name: competition?.label },
      };

      return flattenAndAddPrefix(objSnakeToCamel(gameWithAggs), '.', false);
    });

  const sortedGamesToDisplay = sortObjectArray(orderBy, gamesToDisplayWithAggs, i18next.language, orderDirection);
  return limitArray(sortedGamesToDisplay, pageAndOrderParams);
});

export const teamGameAggsCountAtom = atom(async get => get(unwrappedTeamSelectedGamesForQueryAtom).length);
